"use client";

import React, { useState } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import { MilestoneList } from "@/components/projects/milestone-list";
import {
  updateProjectAction,
  deleteProjectAction,
} from "@/actions/projects-actions";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useBreadcrumbStore } from "@/components/layout/breadcrumb";
import { ArrowLeft, User, Trash2, Edit3, Euro, Sparkles } from "lucide-react";

interface ProjectDetail {
  id: string;
  title: string;
  budget: number;
  revenue: number;
  status: "on_track" | "watch" | "at_risk" | "blocked";
  ownerId: string | null;
  ownerName: string | null;
}

interface MilestoneItem {
  id: string;
  projectId: string;
  title: string;
  dueDate: string;
  status: "todo" | "done" | "delayed";
}

interface AgentItem {
  id: string;
  name: string;
}

interface ProjectDetailsClientProps {
  project: ProjectDetail;
  milestones: MilestoneItem[];
  agents: AgentItem[];
}

export function ProjectDetailsClient({
  project,
  milestones,
  agents,
}: ProjectDetailsClientProps) {
  const router = useRouter();
  const setLabel = useBreadcrumbStore((state) => state.setLabel);
  React.useEffect(() => {
    if (project?.title) {
      setLabel(project.id, project.title);
    }
  }, [project?.id, project?.title, setLabel]);

  const [isEditProjectOpen, setIsEditProjectOpen] = useState(false);

  // Project Form State
  const [projectTitle, setProjectTitle] = useState(project.title);
  const [projectBudget, setProjectBudget] = useState(project.budget.toString());
  const [projectRevenue, setProjectRevenue] = useState(
    project.revenue.toString(),
  );
  const [projectStatus, setProjectStatus] = useState(project.status);
  const [projectOwnerId, setProjectOwnerId] = useState(project.ownerId || "");
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectError, setProjectError] = useState("");

  const handleEditProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setProjectError("");

    if (!projectTitle.trim()) {
      setProjectError("Il titolo del progetto è obbligatorio.");
      return;
    }
    const budgetNum = parseFloat(projectBudget);
    const revenueNum = parseFloat(projectRevenue);

    if (isNaN(budgetNum) || budgetNum < 0) {
      setProjectError(
        "Il budget deve essere un numero valido maggiore o uguale a 0.",
      );
      return;
    }
    if (isNaN(revenueNum) || revenueNum < 0) {
      setProjectError(
        "Il fatturato previsto deve essere un numero valido maggiore o uguale a 0.",
      );
      return;
    }

    setProjectLoading(true);
    try {
      const res = await updateProjectAction({
        id: project.id,
        title: projectTitle,
        budget: budgetNum,
        revenue: revenueNum,
        status: projectStatus,
        ownerId: projectOwnerId || null,
      });

      if (res.error) {
        setProjectError(res.error);
      } else {
        setIsEditProjectOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setProjectError(err.message || "Impossibile aggiornare il progetto.");
    } finally {
      setProjectLoading(false);
    }
  };

  const handleDeleteProject = async () => {
    if (
      !confirm(
        "Sei sicuro di voler eliminare questo progetto? Questa operazione eliminerà anche tutte le relative milestone e attività collegate.",
      )
    )
      return;

    try {
      const res = await deleteProjectAction(project.id);
      if (res.error) {
        alert(res.error);
      } else {
        router.push("/work/projects");
      }
    } catch (err: any) {
      alert(err.message || "Errore durante l'eliminazione del progetto.");
    }
  };

  const getStatusBadge = (status: ProjectDetail["status"]) => {
    switch (status) {
      case "on_track":
        return <Badge variant="success">On Track</Badge>;
      case "watch":
        return <Badge variant="warning">In Osservazione</Badge>;
      case "at_risk":
        return <Badge variant="error">A Rischio</Badge>;
      case "blocked":
        return <Badge variant="error">Bloccato</Badge>;
    }
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  return (
    <div className="space-y-6">
      {/* Back button & Action buttons */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Link
          href="/work/projects"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Torna all'elenco
        </Link>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditProjectOpen(true)}
            className="gap-1.5"
          >
            <Edit3 className="h-4 w-4" />
            Modifica
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDeleteProject}
            className="gap-1.5"
          >
            <Trash2 className="h-4 w-4" />
            Elimina
          </Button>
        </div>
      </div>

      {/* Project Detail Card */}
      <Card className="border-t-4 border-t-category-work">
        <CardHeader className="pb-4">
          <div className="flex justify-between items-start gap-4">
            <div className="space-y-1">
              <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                Scheda Progetto
              </span>
              <h2 className="text-2xl font-bold tracking-tight text-text-primary leading-tight">
                {project.title}
              </h2>
            </div>
            <div className="shrink-0">{getStatusBadge(project.status)}</div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Quick Metrics & Financial Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div className="space-y-1.5 bg-card-muted/30 p-4 rounded-xl border border-border/50">
              <span className="text-text-secondary text-xs font-semibold uppercase tracking-wider">
                Proprietario
              </span>
              {project.ownerId ? (
                <Link
                  href={`/people/${project.ownerId}`}
                  className="flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors font-bold mt-1 group"
                >
                  <div className="h-7 w-7 rounded-full bg-primary-600/10 text-primary-600 flex items-center justify-center text-xs font-bold border border-primary-600/20 group-hover:bg-primary-600/20 transition-colors">
                    <User className="h-3.5 w-3.5" />
                  </div>
                  <span className="truncate hover:underline">
                    {project.ownerName || "Non assegnato"}
                  </span>
                </Link>
              ) : (
                <div className="flex items-center gap-2 text-text-secondary font-bold mt-1">
                  <div className="h-7 w-7 rounded-full bg-card-muted text-text-muted flex items-center justify-center text-xs font-bold border border-border">
                    <User className="h-3.5 w-3.5" />
                  </div>
                  <span className="truncate">Non assegnato</span>
                </div>
              )}
            </div>

            <div className="space-y-1.5 bg-card-muted/30 p-4 rounded-xl border border-border/50">
              <span className="text-text-secondary text-xs font-semibold uppercase tracking-wider">
                Budget Stanziato
              </span>
              <div className="flex items-center gap-1.5 text-text-primary font-extrabold mt-1 text-lg">
                <Euro className="h-4 w-4 text-text-secondary" />
                {formatCurrency(project.budget)}
              </div>
            </div>

            <div className="space-y-1.5 bg-card-muted/30 p-4 rounded-xl border border-border/50">
              <span className="text-text-secondary text-xs font-semibold uppercase tracking-wider">
                Fatturato Generato
              </span>
              <div className="flex items-center gap-1.5 text-success font-extrabold mt-1 text-lg">
                <Euro className="h-4 w-4 text-success/60" />
                {formatCurrency(project.revenue)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Milestones List Component */}
      <div className="bg-card p-6 rounded-xl border border-border">
        <MilestoneList projectId={project.id} milestones={milestones} />
      </div>

      {/* Edit Project Dialog */}
      <Dialog open={isEditProjectOpen} onOpenChange={setIsEditProjectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Progetto</DialogTitle>
            <DialogDescription>
              Aggiorna le informazioni operative e i valori finanziari del
              progetto.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEditProject} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-proj-title">Nome del Progetto</Label>
              <Input
                id="edit-proj-title"
                value={projectTitle}
                onChange={(e) => setProjectTitle(e.target.value)}
                disabled={projectLoading}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="edit-proj-budget">Budget Allocato (€)</Label>
                <Input
                  id="edit-proj-budget"
                  type="number"
                  value={projectBudget}
                  onChange={(e) => setProjectBudget(e.target.value)}
                  disabled={projectLoading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-proj-revenue">
                  Fatturato Previsto (€)
                </Label>
                <Input
                  id="edit-proj-revenue"
                  type="number"
                  value={projectRevenue}
                  onChange={(e) => setProjectRevenue(e.target.value)}
                  disabled={projectLoading}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="edit-proj-owner">Proprietario (Owner)</Label>
                <Select
                  id="edit-proj-owner"
                  value={projectOwnerId}
                  onChange={(e) => setProjectOwnerId(e.target.value)}
                  disabled={projectLoading}
                >
                  <option value="">Nessuno (Non assegnato)</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-proj-status">Stato Progetto</Label>
                <Select
                  id="edit-proj-status"
                  value={projectStatus}
                  onChange={(e) => setProjectStatus(e.target.value as any)}
                  disabled={projectLoading}
                >
                  <option value="on_track">In Corso (On Track)</option>
                  <option value="watch">In Osservazione</option>
                  <option value="at_risk">A Rischio</option>
                  <option value="blocked">Bloccato</option>
                </Select>
              </div>
            </div>

            {projectError && <ErrorMessage>{projectError}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsEditProjectOpen(false)}
                disabled={projectLoading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={projectLoading}>
                Salva Modifiche
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
